// View-model types and wire→view-model translation for the Objectives page's Farm → Barn
// [→ Section] list, plus the two business rules that operate on those rows (a Farm's uniform
// target caption, and a Barn's trend-history direction). Mirrors active-groups/sectionRow.ts's
// job for its own page. Display/colour/format concerns live in objectiveDisplay.ts instead.
//
// Pure, no Vue/store dependencies, so every export here is unit-testable in isolation.

import type { BarnObjective, FarmMortalityHistory, FarmObjectiveGroup, MortalityHistoryPoint, ObjectiveStatus, SectionObjective, TargetSource } from '@/types/MortalityObjective';

const KNOWN_TARGET_SOURCES: readonly TargetSource[] = ['Override', 'Default', 'None'];

// Narrows the wire's forward-compat `TargetSource | string` down to the closed set every renderer
// actually branches on. An unrecognized value renders exactly like 'Default' already does today (both
// ObjectiveBar.vue and ObjectiveTrendSparkline.vue only ever check for 'Override'), so defaulting here
// changes nothing about what's shown - it only gives every downstream comparison a type that catches a
// typo'd literal at compile time instead of silently falling through to the wrong branch.
function toTargetSource(value: string): TargetSource {
    return (KNOWN_TARGET_SOURCES as readonly string[]).includes(value) ? (value as TargetSource) : 'Default';
}

// A row's status, narrowed from the wire's ObjectiveStatus once at this boundary - `target_source`
// becomes the closed TargetSource set (see toTargetSource above) rather than carrying the wire's
// forward-compat escape hatch any further downstream than the one place that needs it.
export type RowObjectiveStatus = Omit<ObjectiveStatus, 'target_source'> & { target_source: TargetSource };

function toRowStatus(status: ObjectiveStatus): RowObjectiveStatus {
    return { ...status, target_source: toTargetSource(status.target_source) };
}

export type SectionObjectiveRow = {
    id: number;
    name: string;
    currentValue: number | null;
    deadCount: number;
    resolvedTarget: number | null;
    status: RowObjectiveStatus;
    pigsToTarget: number | null;
};

// `sections` is populated only for an expandable Barn; empty for an aggregated one (see
// BarnObjective in @/types/MortalityObjective).
export type BarnObjectiveRow = {
    id: number;
    name: string;
    isExpandable: boolean;
    currentValue: number | null;
    deadCount: number;
    resolvedTarget: number | null;
    status: RowObjectiveStatus;
    sections: SectionObjectiveRow[];
    pigsToTarget: number | null;
};

// Farm: grouping header only, no computed figure of its own — see BarnRow.vue/SectionRow.vue for
// where the actual figures render.
export type FarmObjectiveGroupRow = {
    id: number;
    name: string;
    barns: BarnObjectiveRow[];
};

// Translates the snake_case wire shapes (types/MortalityObjective.ts) into the camelCase row shapes
// above — the one place in this feature that has to know the wire uses snake_case field names.
function toSectionRow(section: SectionObjective): SectionObjectiveRow {
    return {
        id: section.location_id,
        name: section.name,
        currentValue: section.current_value,
        deadCount: section.dead_count,
        resolvedTarget: section.resolved_target,
        status: toRowStatus(section.status),
        pigsToTarget: section.pigs_to_target,
    };
}

function toBarnRow(barn: BarnObjective): BarnObjectiveRow {
    return {
        id: barn.location_id,
        name: barn.name,
        isExpandable: barn.is_expandable,
        currentValue: barn.current_value,
        deadCount: barn.dead_count,
        resolvedTarget: barn.resolved_target,
        status: toRowStatus(barn.status),
        // Aggregated Barns send `sections: null` from the API (not an empty array - null means not
        // applicable, distinct from measured zero), so normalise here at the wire boundary; every
        // consumer of BarnObjectiveRow downstream can then rely on `sections` always being an array.
        sections: (barn.sections ?? []).map(toSectionRow),
        pigsToTarget: barn.pigs_to_target,
    };
}

export function toFarmGroupRows(farms: FarmObjectiveGroup[]): FarmObjectiveGroupRow[] {
    return farms.map((farm) => ({
        id: farm.location_id,
        name: farm.name,
        // A Farm with no matching-type Barns can come back with `barns` null/absent rather than an
        // empty array — normalise here, same reasoning as toBarnRow's `sections` guard above.
        barns: (farm.barns ?? []).map(toBarnRow),
    }));
}

// A Farm card header shows the resolved target as a caption ("· target 1%") only when every
// Barn/Section under that Farm agrees on the same resolved target (the common case: everything
// inherits one Directory default). Rows that disagree (a Farm override differs from the Directory
// default, or from each other) fall back to per-row tick+tooltip only (see ObjectiveBar.vue) rather
// than showing a caption that would be wrong for some of the rows underneath it. Rows with no target
// at all (null) are excluded from the comparison — they show their own "Set" button/text regardless,
// so they don't count as disagreeing.
export function resolveFarmUniformTarget(farm: FarmObjectiveGroupRow): number | null {
    const targets = farm.barns.flatMap((barn) => [barn.resolvedTarget, ...barn.sections.map((section) => section.resolvedTarget)]).filter((target): target is number => target != null);
    if (targets.length === 0) return null;
    return targets.every((target) => target === targets[0]) ? targets[0] : null;
}

// Trend-history sparkline side-tile: per-Barn, a separate query/response from the Farm/Barn/Section
// overview above, but translated at the same wire boundary for the same reason —
// ObjectiveTrendSparkline.vue only ever sees camelCase. No Farm-level aggregate row — trend
// direction and the chart are both per-Barn only.
export type HistoryPointRow = {
    periodEnd: string;
    currentValue: number | null;
    deadCount: number;
    status: ObjectiveStatus;
    pigsToTarget: number | null;
};

export type BarnHistoryRow = {
    id: number;
    name: string;
    history: HistoryPointRow[];
};

export type FarmHistoryRow = {
    id: number;
    name: string;
    resolvedTarget: number | null;
    targetSource: TargetSource;
    barns: BarnHistoryRow[];
};

function toHistoryPointRow(point: MortalityHistoryPoint): HistoryPointRow {
    return {
        periodEnd: point.period_end,
        currentValue: point.current_value,
        deadCount: point.dead_count,
        status: point.status,
        pigsToTarget: point.pigs_to_target,
    };
}

export function toFarmHistoryRow(farm: FarmMortalityHistory): FarmHistoryRow {
    return {
        id: farm.location_id,
        name: farm.name,
        resolvedTarget: farm.resolved_target,
        targetSource: toTargetSource(farm.target_source),
        barns: (farm.barns ?? []).map((barn) => ({
            id: barn.location_id,
            name: barn.name,
            history: (barn.history ?? []).map(toHistoryPointRow),
        })),
    };
}

// Improvement arrow (gamification): the latest known history bucket compared with the one right
// before it. Named for what happened to the number ("improved"/"worsened"), not for the arrow glyph
// or its colour: lower mortality is better, so a *fall* in value is the good outcome.
export type TrendDirection = 'improved' | 'worsened';

export function computeTrendDirection(history: HistoryPointRow[]): TrendDirection | null {
    const known = history.filter((point): point is HistoryPointRow & { currentValue: number } => point.currentValue != null);
    if (known.length < 2) return null;
    const latest = known[known.length - 1].currentValue;
    const previous = known[known.length - 2].currentValue;
    if (latest === previous) return null;
    return latest < previous ? 'improved' : 'worsened';
}

// Matches a Barn's trend direction against the live-overview row it's rendered alongside — the
// overview (Farm/Barn/Section figures) and the history (this lookup's input map) come from two
// independently-fetched queries, joined here by Farm id then Barn id. Null wherever that Farm/Barn
// pairing has no history loaded yet (still loading, query failed) or computeTrendDirection itself
// has too little history to call a direction.
export function findBarnTrendDirection(historyByFarmId: Map<number, FarmHistoryRow>, farmId: number, barnId: number): TrendDirection | null {
    const history = historyByFarmId.get(farmId)?.barns.find((barn) => barn.id === barnId)?.history;
    return history ? computeTrendDirection(history) : null;
}
